import { HttpStatusCode } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { OrganisationNode } from '@coin/shared/org-tree/util';
import { HttpHelpersService } from '@coin/shared/core/data-access';
import { Organisation, Position, SpecialRole, SpecialRoleType } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { catchError, map, Observable, of } from 'rxjs';
import {
  BusinessPartner as ApiBusinessPartner,
  CountryResponsibility as ApiCountryResponsibility,
  OpenJob,
  Organisation as ApiOrganisation,
  OrganisationsService,
  OrganisationTree,
  Position as ApiPosition,
  SpecialResponsibility as ApiSpecialResponsibility
} from '../../api/generated';
import { MappingHelpers } from '../../shared/helper/mapping-helpers';
import { LoadingError } from '../models/loading-error.enum';

export interface OrgManagementStateModel {
  rootOrganisationId?: string;
  organisationNodes: { [organisationId: string]: OrganisationNode };
  organisationPaths: { [organisationId: string]: ApiOrganisation[] };
  organisationTreeLoading: boolean;
  organisationLoadingError: LoadingError;
  employeeRoles: { [organisationId: string]: SpecialRole[] };
  businessPartnersLoaded: { [organisationId: string]: boolean };
  businessPartnersLoading: boolean;
  specialResponsibilitiesLoaded: { [organisationId: string]: boolean };
  specialResponsibilitiesLoading: boolean;
  countryResponsibilitiesLoaded: { [organisationId: string]: boolean };
  countryResponsibilitiesLoading: boolean;
  positions: { [organisationId: string]: Position[] };
  positionsLoading: { [organisationId: string]: boolean };
  openJobs: { [organisationId: string]: OpenJob[] };
  openJobsLoading: { [organisationId: string]: boolean };
}

const STATE_DEFAULTS: OrgManagementStateModel = {
  organisationNodes: {},
  organisationPaths: {},
  organisationTreeLoading: false,
  organisationLoadingError: undefined,
  employeeRoles: {},
  businessPartnersLoaded: {},
  businessPartnersLoading: false,
  specialResponsibilitiesLoaded: {},
  specialResponsibilitiesLoading: false,
  countryResponsibilitiesLoaded: {},
  countryResponsibilitiesLoading: false,
  positions: {},
  positionsLoading: {},
  openJobs: {},
  openJobsLoading: {}
};

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class OrgManagementState {
  private organisationsService = inject(OrganisationsService);
  private httpHelper = inject(HttpHelpersService);

  private orgManagementStateModel = signal<OrgManagementStateModel>(STATE_DEFAULTS);

  public organisationTreeLoading = computed(() => this.orgManagementStateModel().organisationTreeLoading);

  public rootOrganisationId = computed(() => this.orgManagementStateModel().rootOrganisationId);

  public organisationNodes = computed(() => this.orgManagementStateModel().organisationNodes);

  public readonly organisationPaths = computed(() => this.orgManagementStateModel().organisationPaths);

  public organisationLoadingError = computed(() => this.orgManagementStateModel().organisationLoadingError);

  public businessPartnersLoading = computed(() => this.orgManagementStateModel().businessPartnersLoading);

  public employeeRoles = computed(() => this.orgManagementStateModel().employeeRoles);

  public specialResponsibilitiesLoading = computed(() => this.orgManagementStateModel().specialResponsibilitiesLoading);

  public countryResponsibilitiesLoading = computed(() => this.orgManagementStateModel().countryResponsibilitiesLoading);

  public positions = computed(() => this.orgManagementStateModel().positions);

  public positionsLoading = computed(() => this.orgManagementStateModel().positionsLoading);

  public organisation(organisationId?: string): Signal<Organisation> {
    return computed(() => this.orgManagementStateModel().organisationNodes[organisationId]?.organisation);
  }

  public organisationPath(organisationId?: string): Signal<ApiOrganisation[]> {
    return computed(() => this.orgManagementStateModel().organisationPaths[organisationId]);
  }

  public openJobsLoading(organisationId: string): Signal<boolean> {
    return computed(() => this.orgManagementStateModel().openJobsLoading[organisationId]);
  }

  public openJobs(organisationId: string): Signal<OpenJob[]> {
    return computed(() => this.orgManagementStateModel().openJobs[organisationId]);
  }

  public loadRootOrganisation(): Observable<void> {
    this.orgManagementStateModel.update(state => ({
      ...state,
      organisationTreeLoading: true,
      organisationLoadingError: undefined
    }));

    return this.organisationsService.getRootOrganisationTree().pipe(
      this.httpHelper.handleError('Cannot get organisation tree'),
      map(rootOrg => this.rootOrganisationLoaded(rootOrg))
    );
  }

  private rootOrganisationLoaded(rootOrganisation: OrganisationTree): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.rootOrganisationId = rootOrganisation.root.id;
        const updatedRootOrganisation = {
          ...rootOrganisation,
          root: rootOrganisation.root,
          children: rootOrganisation.children
        };
        draft.organisationNodes[updatedRootOrganisation.root.id] =
          OrgManagementState.mapOrganisationNode(updatedRootOrganisation);
        draft.organisationPaths[updatedRootOrganisation.root.id] = [
          ...updatedRootOrganisation.path,
          updatedRootOrganisation.root
        ];
        draft.organisationTreeLoading = false;
      })
    );
  }

  public loadOrganisationTree(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().organisationNodes[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state => ({
      ...state,
      organisationTreeLoading: true,
      organisationLoadingError: undefined
    }));

    return this.organisationsService.getOrganisationTree(organisationId).pipe(
      catchError(response => {
        const loadingError = response.status === HttpStatusCode.NotFound ? LoadingError.NotFound : LoadingError.Other;
        this.orgManagementStateModel.update(state => ({
          ...state,
          organisationTreeLoading: false,
          organisationLoadingError: loadingError
        }));
        throw response;
      }),
      map(orgTree => this.organisationTreeLoaded(organisationId, orgTree))
    );
  }

  private organisationTreeLoaded(organisationId: string, organisationTree: OrganisationTree): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.organisationNodes[organisationId] = OrgManagementState.mapOrganisationNode(organisationTree);
        draft.organisationPaths[organisationId] = [...organisationTree.path, organisationTree.root];
        draft.organisationTreeLoading = false;
        draft.organisationLoadingError = undefined;
      })
    );
  }

  public loadBusinessPartners(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().businessPartnersLoaded[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state => ({ ...state, businessPartnersLoading: true }));

    return this.organisationsService.getBusinessPartners(organisationId).pipe(
      this.httpHelper.handleError('Cannot get business partners for organisation'),
      map(businessPartners => this.businessPartnersLoaded(organisationId, businessPartners.content))
    );
  }

  private businessPartnersLoaded(organisationId: string, businessPartners: ApiBusinessPartner[]): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.businessPartnersLoaded[organisationId] = true;
        draft.employeeRoles[organisationId] = [
          ...(draft.employeeRoles[organisationId] ?? []),
          ...businessPartners.map(role => OrgManagementState.mapSpecialRole(role, SpecialRoleType.BusinessPartner))
        ];
        draft.businessPartnersLoading = false;
      })
    );
  }

  public loadSpecialResponsibilities(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().specialResponsibilitiesLoaded[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state => ({ ...state, specialResponsibilitiesLoading: true }));

    return this.organisationsService.getSpecialResponsibilities(organisationId).pipe(
      this.httpHelper.handleError('Cannot get special responsibilities for organisation'),
      map(specialResponsibilities =>
        this.specialResponsibilitiesLoaded(organisationId, specialResponsibilities.content)
      )
    );
  }

  private specialResponsibilitiesLoaded(
    organisationId: string,
    specialResponsibilities: ApiSpecialResponsibility[]
  ): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.specialResponsibilitiesLoaded[organisationId] = true;
        draft.employeeRoles[organisationId] = [
          ...(draft.employeeRoles[organisationId] ?? []),
          ...specialResponsibilities.map(role =>
            OrgManagementState.mapSpecialRole(role, SpecialRoleType.SpecialResponsibility)
          )
        ];
        draft.specialResponsibilitiesLoading = false;
      })
    );
  }

  public loadCountryResponsibilities(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().countryResponsibilitiesLoaded[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state => ({ ...state, countryResponsibilitiesLoading: true }));

    return this.organisationsService.getCountryResponsibilities(organisationId).pipe(
      this.httpHelper.handleError('Cannot get country responsibilities for organisation'),
      map(countryResponsibilities =>
        this.countryResponsibilitiesLoaded(organisationId, countryResponsibilities.content)
      )
    );
  }

  private countryResponsibilitiesLoaded(
    organisationId: string,
    countryResponsibilities: ApiCountryResponsibility[]
  ): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.countryResponsibilitiesLoaded[organisationId] = true;
        draft.employeeRoles[organisationId] = [
          ...(draft.employeeRoles[organisationId] ?? []),
          ...countryResponsibilities.map(role =>
            OrgManagementState.mapSpecialRole(role, SpecialRoleType.CountryResponsibility)
          )
        ];
        draft.countryResponsibilitiesLoading = false;
      })
    );
  }

  public loadPositions(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().positions[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.positionsLoading[organisationId] = true;
      })
    );

    return this.organisationsService.getPositions(organisationId).pipe(
      this.httpHelper.handleError('Cannot get positions for organisation'),
      map(positions => this.positionsLoaded(organisationId, positions))
    );
  }

  private positionsLoaded(organisationId: string, positions: ApiPosition[]): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.positions[organisationId] = positions.map(MappingHelpers.mapPosition);
        draft.positionsLoading[organisationId] = false;
      })
    );
  }

  public loadOpenJobs(organisationId: string): Observable<void> {
    if (this.orgManagementStateModel().openJobs[organisationId]) {
      return of(undefined);
    }

    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.openJobsLoading[organisationId] = true;
      })
    );

    return this.organisationsService.getOpenJobs(organisationId).pipe(
      this.httpHelper.handleError('Cannot get open jobs for organisation'),
      map(openJobs => this.openJobsLoaded(organisationId, openJobs))
    );
  }

  private openJobsLoaded(organisationId: string, openJobs: OpenJob[]): void {
    this.orgManagementStateModel.update(state =>
      produce(state, draft => {
        draft.openJobs[organisationId] = openJobs;
        draft.openJobsLoading[organisationId] = false;
      })
    );
  }

  private static mapOrganisationNode(organisationTree: OrganisationTree): OrganisationNode {
    return {
      organisation: OrgManagementState.mapOrganisation(organisationTree.root),
      children: organisationTree.children?.map(OrgManagementState.mapOrganisation)
    };
  }

  private static mapOrganisation(organisation: ApiOrganisation): Organisation {
    return {
      ...organisation,
      heads: organisation.heads?.map(MappingHelpers.mapPosition)
    };
  }

  private static mapSpecialRole(
    specialRole: ApiBusinessPartner | ApiSpecialResponsibility | ApiCountryResponsibility,
    type: SpecialRoleType
  ): SpecialRole {
    return {
      title: specialRole.title,
      employee: MappingHelpers.mapEmployee(specialRole.employee),
      id: specialRole.employee.id,
      type
    };
  }
}
